import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import type {
  Migration as SchemaMigration,
  MigrationMinimal,
  MigrationAdd,
  MigrationUpdate,
  MigrationDeploymentAdd,
  ScheduledTask
} from '@schema';
import api from '@/common/utils/api';
import useCommonStore, { type FetchOptions } from '@/common/store/pinia/common';
import mapper, { MappedMigration } from '@/common/store/pinia/migrations/migrationsMapper';
import { getCloudInstanceFetchUrl, getDeploymentsUrl, getMigrationsUrl } from '@/common/store/pinia/maasUrls';
import isSelfService from '@/common/utils/isSelfService';
import i18n from '@/common/i18n';
import { store } from '@/common/root';
import { NotificationType } from '@/common/constants/common.constants';
import { takeLastByKeyIgnoreCancel } from '@/common/utils/takeLastByKeyIgnoreCancel';
import { useNotifications } from '@/common/utils/notifications/notificationsPlugin';
import { isNotificationForCurrentProject } from '@/common/utils/notifications/isNotificationForCurrentProject';

export interface CreateDeploymentOptions {
  vmName: string;
  cloneDisks?: boolean;
  forceDeployment?: boolean;
  osMorphing?: boolean;
  saveSnapshot?: boolean;
  start?: boolean;
  windowsUserScript?: string;
  linuxUserScript?: string;
}

export type MigrationAsideNavigationItem = {
  id: string;
  name: string;
};

const MIGRATION_CREATING_NOTIF_ID = 'maas-migration-creating';

interface PendingCreation {
  total: number;
  processed: number;
  failed: number;
}

function toMigrationDeploymentAdd(options: CreateDeploymentOptions): MigrationDeploymentAdd {
  return {
    name: options.vmName,
    clone_disks: options.cloneDisks ?? false,
    force_deployment: options.forceDeployment ?? false,
    os_morphing: options.osMorphing ?? false,
    save_snapshot: options.saveSnapshot ?? false,
    start: options.start ?? false,
    ...(options.windowsUserScript ? { windows_user_script: options.windowsUserScript } : {}),
    ...(options.linuxUserScript ? { linux_user_script: options.linuxUserScript } : {})
  };
}

export const useMigrationsStore = defineStore('migrations', () => {
  const { notify } = useNotifications();

  const commonStore = useCommonStore<SchemaMigration, MappedMigration>({
    getUrl: getMigrationsUrl,
    itemKey: 'id',
    plural: (data) => (data as { data: SchemaMigration[] }).data,
    mapItem: mapper,
    updateMethod: 'patch',
    dataChangeMode: 'direct'
  })();

  const {
    getItems,
    remove,
    fetchItem,
    fetchItemsWithFiltersThrottled
  } = commonStore;

  // Overrides commonStore.fetchLastItem so superseded fetches don't surface as unhandled rejections.
  const fetchLastItem = (id: ID, options: FetchOptions = {}) => takeLastByKeyIgnoreCancel(
    abortController => commonStore.fetchItem(id, { ...options, abortController }),
    `${getMigrationsUrl()}/${id}`
  );

  const pendingCreation = ref<PendingCreation | null>(null);

  const showMigrationCreatingNotification = () => notify({
    id: MIGRATION_CREATING_NOTIF_ID,
    type: NotificationType.PROGRESS,
    message: i18n.t('Creating migration...'),
    showBottom: true,
    operationId: MIGRATION_CREATING_NOTIF_ID
  });

  const showMigrationCreationResultNotification = (total: number, failed: number) => {
    if (failed === 0) {
      notify.success({
        id: MIGRATION_CREATING_NOTIF_ID,
        title: i18n.t('Migration created'),
        message: i18n.tc(
          'Migration successfully created|{num} migrations successfully created',
          total,
          { num: total }
        ),
        showBottom: true,
        operationId: MIGRATION_CREATING_NOTIF_ID
      });
      return;
    }

    notify.error({
      id: MIGRATION_CREATING_NOTIF_ID,
      title: i18n.t('Operation failed'),
      message: i18n.t('Operation failed'),
      showBottom: true,
      operationId: MIGRATION_CREATING_NOTIF_ID
    });
  };

  const fetchInstanceFromCloud = (migrationId: string, cloudId: string, instanceId: string) => api.post(
    getCloudInstanceFetchUrl(cloudId, instanceId),
    { migration_id: migrationId }
  );

  const migrations = computed(() => getItems.value);

  const asideNavigationItems = ref<MigrationAsideNavigationItem[]>([]);

  const fetchAsideNavigation = async () => {
    const response = await api.get<{ data: MigrationMinimal[] }>(getMigrationsUrl());
    asideNavigationItems.value = response?.data?.data.map((row) => ({
      id: row.id ?? '',
      name: row.name ?? ''
    }));
  };

  const clearAsideNavigation = () => {
    asideNavigationItems.value = [];
  };

  const deleteMigration = (id: string) => remove(id);

  const deleteItems = (ids: string[]) => Promise.all(ids.map(id => deleteMigration(id)));

  const cleanupDisks = (id: string) => api.post(`${getMigrationsUrl()}/${id}/cleanup_disks`)
      .then(() => fetchItem(id));

  const createDeployment = (migrationId: string, options: CreateDeploymentOptions) => {
    const body = toMigrationDeploymentAdd(options);
    return api.post(getDeploymentsUrl(migrationId), body)
      .then(() => fetchItem(migrationId));
  };

  const addMigration = (body: MigrationAdd) => api.post<ScheduledTask>(getMigrationsUrl(), body)
    .then((response) => {
      if (isSelfService() && !pendingCreation.value) {
        const bodyProject = body as { project_id?: string; target_project?: { id?: string } };
        const projectId = bodyProject.project_id ?? bodyProject.target_project?.id;
        if (!isNotificationForCurrentProject(projectId)) {
          return response;
        }
        const total = Array.isArray(body.instances) ? body.instances.length : 1;

        pendingCreation.value = { total, processed: 0, failed: 0 };
        showMigrationCreatingNotification();
      }
      return response;
    });

  const updateMigration = (id: string, body: MigrationUpdate) => api.patch(`${getMigrationsUrl()}/${id}`, body)
      .then(() => fetchItem(id));

  const onMaas = (data: SocketEvent<{
    migration_id?: string;
    replication_id?: string;
    project_id?: string;
    target_project?: { id?: string };
  }>) => {
    if (data.type !== 'migration' || !data.object?.migration_id) return;
    const id = data.object.migration_id;
    if (data.operation === 'created' || data.operation === 'updated' || data.operation === 'failed') {
      fetchItemsWithFiltersThrottled(id);
    } else if (data.operation === 'deleted') {
      fetchItemsWithFiltersThrottled.clearId(id);
      commonStore.removeItem(id);
    }

    if (isSelfService()
      && pendingCreation.value
      && (data.operation === 'created' || data.operation === 'failed')
      && isNotificationForCurrentProject(
        data.object.project_id ?? data.object.target_project?.id
      )) {
      const pending = pendingCreation.value;
      pending.processed++;
      if (data.operation === 'failed') pending.failed++;

      if (pending.processed >= pending.total) {
        const { total, failed } = pending;
        pendingCreation.value = null;
        store.dispatch('notifications/REMOVE', MIGRATION_CREATING_NOTIF_ID);
        showMigrationCreationResultNotification(total, failed);
      }
    }
  };

  // `maas` channel is not delivered in Self-Service Portal; refresh migration rows from compute-channel
  // `migration_replications` / `migration_deployments` events.
  const onCompute = (data: SocketEvent<{
    migration_id?: string;
    project_id?: string;
    target_project?: { id?: string };
  }>) => {
    const obj = data.object;
    if (!obj) return;
    if (!isNotificationForCurrentProject(obj.project_id ?? obj.target_project?.id)) return;
    if (data.type !== 'migration_replications' && data.type !== 'migration_deployments') return;
    const migrationId = obj.migration_id;
    if (migrationId) fetchLastItem(migrationId);
  };

  return {
    ...commonStore,
    fetchLastItem,
    migrations,
    asideNavigationItems,
    fetchAsideNavigation,
    clearAsideNavigation,
    deleteMigration,
    deleteItems,
    cleanupDisks,
    createDeployment,
    fetchInstanceFromCloud,
    addMigration,
    updateMigration,
    onMaas,
    onCompute
  };
}, {
  ws: {
    maas: 'onMaas',
    compute: 'onCompute'
  }
});

export default useMigrationsStore;
